import * as React from 'react';
import { VaiIcon } from '@vaisala/rockhopper-design-tokens';
import { addClass, Bem } from '../../utils/css.js';
import { Size } from '../../constants.js';
import { useId } from '../../utils/hooks/useId.js';
import './PasswordInput.scss.js';
import { Icon } from '../Icon/Icon.js';
import { StrengthIndicator } from './StrengthIndicator.js';
import { StrengthAdornment } from './StrengthAdornment.js';
import { Input } from './Input.js';

var PasswordStrength;
(function(PasswordStrength2) {
  PasswordStrength2["TooShort"] = "short";
  PasswordStrength2["Weak"] = "weak";
  PasswordStrength2["Fair"] = "fair";
  PasswordStrength2["Good"] = "good";
  PasswordStrength2["Strong"] = "strong";
})(PasswordStrength || (PasswordStrength = {}));
const bem = Bem.create("vai-password-input");
const bemAdornment = Bem.create("vai-adornment");
const bemIconButton = Bem.create("vai-icon-button");
const IconButton = React.forwardRef(function IconButton2(props, ref) {
  const { htmlId, children, disabled = false, className, type = "button", dataTa = "vai-icon-button", ...rest } = props;
  const id = useId(htmlId, ["icon button"]);
  return /* @__PURE__ */ React.createElement("button", {
    id: id.toString(),
    "data-ta": dataTa,
    className: addClass(bemIconButton.block(), className),
    disabled,
    ref,
    type,
    ...rest
  }, children);
});
const onMouseUp = (_e, inputRef) => {
  if (inputRef.current !== null) {
    inputRef.current.focus();
  }
};
const handleFocus = (e, setFocus, onFocus) => {
  setFocus(true);
  if (onFocus) {
    onFocus(e);
  }
};
const handleBlur = (e, setFocus, onBlur) => {
  setFocus(false);
  if (onBlur) {
    onBlur(e);
  }
};
const onRevealClick = (_e, setPasswordVisibility, passwordVisibility) => {
  setPasswordVisibility(!passwordVisibility);
};
const PasswordInput = React.forwardRef((props, ref) => {
  const {
    htmlId,
    invalid,
    dataTa = "vai-password-input",
    width = Size.L,
    type,
    autoComplete = "current-password",
    name = "password",
    readOnly = false,
    onChange,
    innerRef,
    inputProps,
    strength,
    strengthStrings,
    className,
    disabled,
    value,
    onFocus,
    onBlur,
    translations = {
      revealPassword: "Reveal password",
      hidePassword: "Hide password"
    },
    ...rest
  } = props;
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [focus, setFocus] = React.useState(false);
  const id = useId(htmlId, ["password input"]);
  const finalId = React.useMemo(() => Array.isArray(htmlId) ? id.toHtmlId() : id.toString(), [id, htmlId]);
  const inputType = passwordVisible ? "text" : "password";
  const inputRef = React.useRef(null);
  React.useEffect(() => {
    if (inputRef.current !== null) {
      inputRef.current.selectionStart = inputRef.current.value.length;
      inputRef.current.selectionEnd = inputRef.current.value.length;
    }
  }, [inputType]);
  return /* @__PURE__ */ React.createElement(Input, {
    ref,
    dataTa,
    htmlId: finalId,
    className: addClass(bem.block(focus && "focused", disabled && "disabled"), className),
    name,
    innerRef: innerRef ? innerRef : inputRef,
    type: type ? type : inputType,
    disabled,
    invalid,
    value,
    onFocus: (e) => handleFocus(e, setFocus, onFocus),
    onBlur: (e) => handleBlur(e, setFocus, onBlur),
    readOnly,
    onChange,
    autoComplete: passwordVisible ? "off" : autoComplete,
    spellCheck: false,
    inputProps: { autoCapitalize: "off", ...inputProps },
    width,
    ...rest,
    endIcon: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StrengthAdornment, {
      strength,
      strengthStrings
    }), !disabled && /* @__PURE__ */ React.createElement(IconButton, {
      onClick: (e) => onRevealClick(e, setPasswordVisible, passwordVisible),
      className: bemAdornment.element("button")(),
      onMouseUp: (e) => onMouseUp(e, inputRef),
      "aria-label": passwordVisible ? translations.hidePassword : translations.revealPassword
    }, /* @__PURE__ */ React.createElement(Icon, {
      className: bemAdornment.element("icon")(),
      name: passwordVisible ? VaiIcon.VisibilityOff : VaiIcon.VisibilityOn
    }))),
    wrapperChildren: /* @__PURE__ */ React.createElement(StrengthIndicator, {
      strength
    })
  });
});
PasswordInput.metaProperties = ["invalid", "disabled"];

export { PasswordInput, PasswordStrength };
